const topic = (key) => ({ type: 'topic', key });
/** Build the canonical `TriggerExpr` for a phrase (see the table above). */
export const fromPhrase = (p) => {
    if (p.mode === 'always')
        return { type: 'always' };
    const nots = p.exclude.map((k) => ({ type: 'not', of: topic(k) }));
    const required = (p.require ?? []).map(topic);
    const positives = p.match === 'any' ? [{ type: 'any', of: p.include.map(topic) }] : p.include.map(topic);
    if (nots.length === 0 && required.length === 0) {
        if (p.match === 'any')
            return positives[0];
        if (positives.length === 1)
            return positives[0];
    }
    return { type: 'all', of: [...positives, ...nots, ...required] };
};
/** Validation messages for a phrase being edited (empty array = valid). */
export const validatePhrase = (p) => {
    if (p.mode === 'always')
        return [];
    const errors = [];
    if (p.include.length === 0) {
        errors.push(p.exclude.length > 0
            ? 'Укажите хотя бы одну тему, при которой запускать — одного «кроме» недостаточно'
            : 'Укажите хотя бы одну тему');
    }
    const require = p.require ?? [];
    if ([...p.include, ...require, ...p.exclude].some((k) => k.trim() === '')) {
        errors.push('Пустая тема — выберите тему из списка');
    }
    const lists = [
        ['условии', p.include],
        ['«и обязательно»', require],
        ['«кроме»', p.exclude],
    ];
    for (const [name, keys] of lists) {
        const dupes = new Set(keys.filter((k, i) => keys.indexOf(k) !== i));
        for (const k of dupes)
            errors.push(`Тема «${k}» дважды указана в ${name}`);
    }
    for (let i = 0; i < lists.length; i++) {
        for (let j = i + 1; j < lists.length; j++) {
            const [nameA, keysA] = lists[i];
            const [nameB, keysB] = lists[j];
            const other = new Set(keysB);
            for (const k of new Set(keysA)) {
                if (!other.has(k))
                    continue;
                errors.push(nameB === '«кроме»'
                    ? `Тема «${k}» одновременно в ${nameA} и в «кроме» — пайплайн никогда не запустится`
                    : `Тема «${k}» одновременно в ${nameA} и в ${nameB} — оставьте в одном месте`);
            }
        }
    }
    return errors;
};
/**
 * Structural equality of two trigger expressions: object key order is
 * irrelevant, child order is significant (it is what `fromPhrase` reproduces).
 */
export const triggerEquals = (a, b) => {
    switch (a.type) {
        case 'always':
            return b.type === 'always';
        case 'topic':
            return b.type === 'topic' && a.key === b.key;
        case 'not':
            return b.type === 'not' && triggerEquals(a.of, b.of);
        case 'all':
        case 'any':
            return (b.type === a.type &&
                a.of.length === b.of.length &&
                a.of.every((child, i) => triggerEquals(child, b.of[i])));
    }
};
const topicKeysOf = (list) => {
    const keys = [];
    for (const e of list) {
        if (e.type !== 'topic')
            return null;
        keys.push(e.key);
    }
    return keys;
};
/** Best-effort guess of the phrase an expression would be; verified by the caller. */
const guessPhrase = (expr) => {
    switch (expr.type) {
        case 'always':
            return { mode: 'always' };
        case 'topic':
            return { mode: 'topics', match: 'all', include: [expr.key], require: [], exclude: [] };
        case 'any': {
            const include = topicKeysOf(expr.of);
            return include ? { mode: 'topics', match: 'any', include, require: [], exclude: [] } : null;
        }
        case 'all': {
            // Bare topics: with an any-group they are all `require`; without one, those
            // before the first `not` are `include` and those after it are `require`.
            const leading = [];
            const trailing = [];
            const exclude = [];
            const anyGroups = [];
            for (const child of expr.of) {
                if (child.type === 'topic')
                    (exclude.length > 0 ? trailing : leading).push(child.key);
                else if (child.type === 'not' && child.of.type === 'topic')
                    exclude.push(child.of.key);
                else if (child.type === 'any') {
                    const keys = topicKeysOf(child.of);
                    if (!keys)
                        return null;
                    anyGroups.push(keys);
                }
                else
                    return null;
            }
            if (anyGroups.length === 0) {
                return { mode: 'topics', match: 'all', include: leading, require: trailing, exclude };
            }
            if (anyGroups.length === 1) {
                const include = anyGroups[0];
                return {
                    mode: 'topics',
                    match: 'any',
                    include,
                    require: [...leading, ...trailing],
                    exclude,
                };
            }
            return null;
        }
        case 'not':
            return null;
    }
};
/**
 * Read an expression as a phrase.
 *  - `null` (manual-only pipeline) → `null`;
 *  - expressible AND round-trips exactly AND passes `validatePhrase` → phrase;
 *  - anything else → `'advanced'` (open the tree editor, do not touch the expression).
 *
 * The round-trip check is the authority, not the guesser: e.g. `{all:[{not:b}, a]}`
 * reads as «a, кроме b», but the canonical form would reorder it to
 * `{all:[a, {not:b}]}`, so it is reported as advanced.
 */
export const toPhrase = (expr) => {
    if (expr === null)
        return null;
    const guess = guessPhrase(expr);
    if (!guess)
        return 'advanced';
    if (validatePhrase(guess).length > 0)
        return 'advanced';
    return triggerEquals(fromPhrase(guess), expr) ? guess : 'advanced';
};
const quote = (key, topicName) => `«${topicName(key)}»`;
const listRu = (items, conj) => items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} ${conj} ${items.at(-1)}`;
const describeNode = (expr, topicName, nested) => {
    switch (expr.type) {
        case 'always':
            return 'любая классификация';
        case 'topic':
            return quote(expr.key, topicName);
        case 'not':
            return `не ${describeNode(expr.of, topicName, true)}`;
        case 'all':
        case 'any': {
            if (expr.of.length === 1)
                return describeNode(expr.of[0], topicName, nested);
            const text = expr.of
                .map((c) => describeNode(c, topicName, true))
                .join(expr.type === 'all' ? ' и ' : ' или ');
            return nested ? `(${text})` : text;
        }
    }
};
/**
 * Human-readable Russian description of any trigger, for the pipeline list and
 * «Читать как текст». Phrase-expressible triggers read as the phrase
 * («Тема «Возврат» или «Спор», и «Угроза жалобы», кроме «Отмена»»); everything else is
 * rendered recursively («(«A» или «B») и не «C»»). `topicName` maps a label
 * key to its display name (fall back to the key for unknown topics).
 */
export const describeTriggerRu = (expr, topicName) => {
    if (expr === null)
        return 'Только вручную';
    const phrase = toPhrase(expr);
    if (phrase === null)
        return 'Только вручную';
    if (phrase === 'advanced') {
        const text = describeNode(expr, topicName, false);
        return text.charAt(0).toUpperCase() + text.slice(1);
    }
    if (phrase.mode === 'always')
        return 'При любой классификации';
    const names = phrase.include.map((k) => quote(k, topicName));
    const head = names.length === 1
        ? `Тема ${names[0]}`
        : phrase.match === 'any'
            ? `Тема ${listRu(names, 'или')}`
            : `Все темы: ${listRu(names, 'и')}`;
    const required = (phrase.require ?? []).map((k) => quote(k, topicName));
    const withRequired = required.length === 0 ? head : `${head}, и ${listRu(required, 'и')}`;
    if (phrase.exclude.length === 0)
        return withRequired;
    return `${withRequired}, кроме ${phrase.exclude.map((k) => quote(k, topicName)).join(', ')}`;
};
//# sourceMappingURL=trigger-phrase.js.map